import { startTransition } from 'react';

import { TRIAL_OFFERS } from '@authFlow/App/constants';
import history from '@authFlow/App/history';
import { isMobileDevice } from '@authFlow/utils/detectMobileDevice';
import { memoryStorageManager } from '@authFlow/utils/MemoryStorageManager';
import { sendSignupFlowEvent } from '@authFlow/utils/signupFlowEvent';
import { Custom } from '@authFlow/utils/authFlowFlags';
import sendSignupGA4Event from '@authFlow/utils/analytics/sendSignupGA4Event';
import {
  getProductGroup,
  getTrialOfferInfo,
  isSSTUser,
  userHasLimits,
} from '@authFlow/utils/requestsToBackend';

import { redirectAndClear } from './redirect';
import { storageManager } from './SessionStorageManager';

import type { StorageKey } from './SessionStorageManager';
import type { AuthMethodT } from '@authFlow/types/types';
import type { PageCustomisationsT, PlanT } from '@authFlow/pages/Plans/types';

const CONTACT_INFO_FORM_COUNTRIES = ['us', 'ca'];

export const initStorageManager = () => {
  const reset = ['/signup/', '/login/'].includes(window.location.pathname);
  storageManager.init(reset);
};

export const isStepRequired = (stepName: StorageKey) => {
  if (storageManager.get(stepName) === 'force') {
    return true;
  }

  if (storageManager.get(stepName) === 'off') {
    return false;
  }

  if (
    storageManager.get('isNew') === true &&
    storageManager.get(stepName) === 'new'
  ) {
    return true;
  }

  return false;
};

export async function getHashedEmail(email: string) {
  try {
    const utf8 = new TextEncoder().encode(email);
    return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray
        .map((bytes) => bytes.toString(16).padStart(2, '0'))
        .join('');
    });
  } catch (error) {
    console.error(error);
    return '';
  }
}

export async function sendSignupAnalytics(userId: string, email: string) {
  const email_hash = await getHashedEmail(email);

  if (window.ga) {
    window.ga('set', 'userId', userId);
    window.ga('set', 'dimension12', userId);
    window.ga('set', '&email', email_hash);
  }

  if (
    typeof window.dataLayer !== 'undefined' &&
    // eslint-disable-next-line no-prototype-builtins
    window.dataLayer.hasOwnProperty('push')
  ) {
    // this is important event! https://kb.semrush.net/x/BCvfAQ
    window.dataLayer.push({ event: 'Registration' });
  }
}

export async function afterSignupStepRedirect() {
  await updateStorageAfterSignup();

  if (storageManager.get('offer')) {
    redirectToSubscribe();
    return;
  }

  if (storageManager.get('promo') || isCustom(Custom.promo)) {
    redirectToPromoPage();
    return;
  }

  if (isStepRequired('survey')) {
    moveToPageReducer('/signup/customize-flow/');
    return;
  }

  if (isStepRequired('toolkitSelection') && isStepRequired('trial')) {
    moveToPageReducer('/signup/toolkit-selection/');
    return;
  }

  if (isStepRequired('trial')) {
    moveToPageReducer('/signup/get-free-trial/');
    return;
  }

  if (isStepRequired('getSubscription')) {
    moveToPageReducer('/signup/get-subscription/');
    return;
  }

  redirectAndClear();
}

export async function afterLoginStepRedirect() {
  const { offer, trial, promo, toolkit } = storageManager.getAll();
  const isTrialForce = trial === 'force';
  const redirectToPromo = promo || isCustom(Custom.promo);

  if (offer || redirectToPromo || isTrialForce) {
    if (await isSSTUser()) {
      redirectAndClear();
      return;
    }
  }

  await updateStorageAfterLogin();

  if (offer) {
    redirectToSubscribe();
    return;
  }

  if (redirectToPromo) {
    redirectToPromoPage();
    return;
  }

  if (isStepRequired('survey')) {
    moveToPageReducer('/signup/customize-flow/');
    return;
  }

  if (isStepRequired('toolkitSelection') && isStepRequired('trial')) {
    moveToPageReducer('/signup/toolkit-selection/');
    return;
  }

  if (isStepRequired('trial')) {
    if (toolkit) {
      moveToPageReducer('/signup/get-free-trial/');
      return;
    }

    // we should not allow a user with ACA to buy something on the /subscribe
    // because it will remove all previous ACA limits
    const hasLimits = await userHasLimits();
    if (hasLimits) {
      const redirectTo = getUrlParamsFromStorage(['redirectTo']);
      const redirectToParam = redirectTo ? `?${redirectTo}` : '';
      sendSignupFlowEvent('flow:finish');
      sendSignupGA4Event('send', {
        item_label: 'finish',
        status: 'success',
      });
      storageManager.removeAll();
      return window.location.assign(`/pricing/${redirectToParam}`);
    }
    moveToPageReducer('/signup/get-free-trial/' + window.location.search);
    return;
  }

  redirectAndClear();
}

export function afterPaymentStepRedirect() {
  redirectAndClear();
}

export function redirectToSubscribe() {
  moveToPageReducer(
    `/subscribe/?${getUrlParamsFromStorage([
      'offer',
      'promocode',
      'currency',
      'promo',
      'redirectTo',
    ])}`,
  );
}

export function redirectToPromoPage() {
  moveToPageReducer(
    `/signup/promo/?${getUrlParamsFromStorage(['promo', 'redirectTo'])}`,
  );
}

export function getRegistrationSource() {
  const custom = storageManager.get('custom');
  const registerSource = storageManager.get('src');

  if (custom === Custom.secure) {
    return 'secure';
  }

  if (custom === Custom.promo) {
    return 'promo';
  }

  if (registerSource) {
    return registerSource.replace(/[^A-Za-z0-9_-]+$/g, '').toLowerCase();
  }

  return 'semrush';
}

export function getUrlParamsFromStorage(keys: StorageKey[]) {
  const params = {};

  keys.forEach((key: StorageKey) => {
    const value = storageManager.get(key);
    if (value) {
      params[camelToSnakeCase(key)] = value;
    }
  });

  return new URLSearchParams(params).toString();
}

export function fromStorageToUrlParams() {
  const keys = [
    'offer',
    'promo',
    'promocode',
    'currency',
    'redirectTo',
    'custom',
    'src',
    'tk',
    'survey',
    'trial',
    'toolkit',
  ];
  const params = {};

  keys.forEach((key: StorageKey) => {
    const value = storageManager.get(key);
    if (value && value !== 'new') {
      params[camelToSnakeCase(key)] = value;
    }
  });

  return new URLSearchParams(params).toString();
}

export function camelToSnakeCase(text: string) {
  return text
    .replace(/(.)([A-Z][a-z]+)/, '$1_$2')
    .replace(/([a-z0-9])([A-Z])/, '$1_$2')
    .toLowerCase();
}

// todo [key: CustomTrialTypesT]: string
const customOfferTypes = {
  default: '',
  [Custom.competitiveIntelligence]: 'ci',
  [Custom.backlinko]: 'backlinko',
  [Custom.contentMarketingPlatform]: 'cmp',
  [Custom.extendedTrial]: 'extended',
  [Custom.contentToolkit]: 'content-toolkit',
  [Custom.localToolkit]: 'local-toolkit',
  [Custom.advertisingToolkit]: 'advertising-toolkit',
  [Custom.aiToolkit]: 'ai-toolkit',
  [Custom.socialToolkit]: 'social-toolkit',
  [Custom.trafficToolkit]: 'traffic-and-market-toolkit',
  [Custom.prToolkit]: 'pr-toolkit',
  [Custom.aiSeo]: 'ai-seo',
  personalised: 'personalised',
};

export const generateTrialUrl = (plan: PlanT, type?: PageCustomisationsT) => {
  const customOfferType =
    type && customOfferTypes[type] ? customOfferTypes[type] : '';
  const url = customOfferType ? `${customOfferType}-${plan}` : `${plan}`;
  return `${url}-free-trial`;
};

export const getTrialOfferByUrl = (customURL?: string) => {
  const currentURL = customURL || window.location.pathname;
  const offerUrl = currentURL
    .replace('/signup/get-free-trial/', '')
    .replace('/', '');
  const meaningfulPart = offerUrl.replace('-free-trial', '');
  const parts = meaningfulPart.split('-');
  if (parts.length === 2) {
    const [type, plan] = parts;
    const offerType = Object.keys(customOfferTypes).find(
      (offer) => customOfferTypes[offer] === type,
    );
    if (offerType && TRIAL_OFFERS[offerType])
      return TRIAL_OFFERS[offerType][plan];
  }
  if (parts.length > 2) {
    const plan = parts[parts.length - 1];
    const type = parts.slice(0, parts.length - 1).join('-');
    if (TRIAL_OFFERS[type]) {
      return TRIAL_OFFERS[type][plan];
    }
  }
  const plan = parts[0];
  return TRIAL_OFFERS['ORIGINAL'][plan];
};

export const isUserContactInfoRequired = (): boolean => {
  // all mobile and promo users skipped this step
  if (
    isMobileDevice() ||
    storageManager.get('promo') ||
    storageManager.get('custom') === Custom.promo ||
    !storageManager.get('isNew')
  )
    return false;

  const userCountry = window?.__AUTH_STATE__?.countryCode?.toLowerCase();
  return userCountry && CONTACT_INFO_FORM_COUNTRIES.includes(userCountry);
};

export const isConsentCheckboxRequired = (authMethod: AuthMethodT): boolean => {
  return (
    storageManager.get('userData')?.isEU &&
    ['google', 'facebook', 'linkedin'].includes(authMethod)
  );
};

export const isSMSConsentRequired = (): boolean => {
  const userCountry = window?.__AUTH_STATE__?.countryCode;
  return userCountry?.toLowerCase() === 'us';
};

export const moveToPageReducer = (
  path: string,
  storageKey?: string,
  payload?: Record<string, any>,
): void => {
  if (storageKey && payload) {
    memoryStorageManager.set({ [storageKey]: payload });
  }
  if (window.location.pathname === '/') {
    window.location.assign(path);
  } else {
    startTransition(() => {
      history.push(path);
    });
  }
};

export const authCommonErrorsHandler = (messageCode: string) => {
  switch (messageCode) {
    case 'ERROR_ACTIVITY_DISABLED_MESSAGE':
      moveToPageReducer(`/login/disable_hard/`);
      return;
    case 'ERROR_ACTIVITY_RESET_MESSAGE':
      moveToPageReducer(`/login/disable_soft/`);
      return;
    case 'ERROR_LOGIN_DISABLED':
      moveToPageReducer(`/login/disable/`);
      return;
  }
};

export const isCustom = (custom: string): boolean => {
  return storageManager.get('custom') === custom;
};

export const setOfferInfo = async (offer: string) => {
  const offerInfo = await getTrialOfferInfo(offer);
  storageManager.set({ offerInfo });
};

export const updateStorageAfterSignup = async () => {
  const offer = storageManager.get('offer');
  if (offer) {
    await setOfferInfo(offer);
  }

  if (offer || !isStepRequired('trial')) {
    storageManager.set({
      marketingSource: 'off',
    });
  }

  if (isStepRequired('survey') && isStepRequired('toolkitSelection')) {
    storageManager.set({
      toolkitSelection: 'off',
    });
  }
};

const updateStorageAfterLogin = async () => {
  if (isStepRequired('survey') && isStepRequired('toolkitSelection')) {
    storageManager.set({
      toolkitSelection: 'off',
    });
  }

  const toolkit = storageManager.get('toolkit');
  const trial = storageManager.get('trial');
  if (toolkit && trial !== 'off') {
    const userProductGroup = await getProductGroup();
    if (userProductGroup === 'free') {
      storageManager.set({
        trial: 'force',
      });
    }
  }

  const offer = storageManager.get('offer');
  if (offer) {
    await setOfferInfo(offer);
  }
};

export const isTabPlansTemplate = () => {
  return (
    storageManager.get('personalisedPlansEligible') ||
    !!storageManager.get('userData')?.personalisedPlan
  );
};
